import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Container,
  Card,
  CardBody,
  Button,
  Input,
  FormGroup,
  Label,
  Row,
  Col,
  FormFeedback
} from "reactstrap";
import SocialIcons from "../../components/socialicons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessage } from "@fortawesome/free-solid-svg-icons";
import api from '../../service/api'; 
import { toast } from "react-toastify";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Estados para validação
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    message: false,
    acceptedTerms: false
  });
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    subject: false,
    message: false,
    acceptedTerms: false
  });

  // Função de validação
  const validateForm = () => {
    const newErrors = {
      name: !name.trim(),
      email: !email.trim() || !isValidEmail(email),
      message: !message.trim(),
      acceptedTerms: !acceptedTerms
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  // Validação de email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Marcar campo como tocado
  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  // Validar campo individualmente
  const validateField = (field, value) => {
    switch(field) {
      case 'name':
        return !value.trim();
      case 'email':
        return !value.trim() || !isValidEmail(value);
      case 'message':
        return !value.trim();
      case 'acceptedTerms':
        return !value;
      default:
        return false;
    }
  };

  // Atualizar estado com validação em tempo real
  const handleChange = (field, value, setter) => {
    setter(value);
    
    // Se o campo já foi tocado, valida em tempo real
    if (touched[field]) {
      setErrors({
        ...errors,
        [field]: validateField(field, value)
      });
    }
  };

  const handleRegister = () => {
    // Marcar todos os campos como tocados
    const allTouched = {
      name: true,
      email: true,
      subject: true,
      message: true,
      acceptedTerms: true
    };
    setTouched(allTouched);

    // Validar o formulário
    if (!validateForm()) {
      toast.error("Por favor, preencha todos os campos obrigatórios corretamente.");
      return;
    }

    const payload = {
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim(),
      message: message.trim(),
      shared: acceptedTerms,
    };

    api.post("/contact", payload)
      .then((resp) => {
        if (resp.status === 201) { 
          const message = `Mensagem encaminhada com sucesso!`;
          toast.success(message);
          clear();
        }
      })
      .catch((error) => {
        console.error("Erro ao enviar mensagem:", error);
        toast.error("Erro ao enviar mensagem. Tente novamente.");
      });
  };

  const clear = () => {
    setName("");
    setEmail("");
    setSubject("");
    setMessage("");
    setAcceptedTerms(false);
    setErrors({
      name: false,
      email: false,
      message: false,
      acceptedTerms: false
    });
    setTouched({
      name: false,
      email: false,
      subject: false,
      message: false,
      acceptedTerms: false
    });
  };

  return (
    <Container className="my-5">
      <div className="min-h-screen w-full flex flex-col items-center justify-start py-20 px-4 bg-[#0f0f0f] text-white">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center mb-5"
        >
          <h1 className="text-center font-bold mb-5 text-white text-font-family">Contato</h1>
          <p className="text-center text-color-site text-font-family">
            Se você tem um projeto em mente ou apenas bater um papo sobre tecnologia e livros. 
            <br/>
            Preencha o formulário abaixo e entrarei em contato em breve.
          </p>
        </motion.div>

        <Row> 
          <Col sm="6">
            {/* Formulário */}
            <Card className="bg-[#151515] border border-gray-800 rounded-2xl">
              <CardBody className="p-6 flex flex-col gap-4">
                <FormGroup>
                  <Label className="text-sm text-gray-300 text-font-family">Nome *</Label>
                  <Input 
                    placeholder="Seu nome" 
                    className={`bg-[#1d1d1d] border-gray-700 ${errors.name && touched.name ? 'border-red-500' : ''}`}
                    value={name} 
                    onChange={e => handleChange('name', e.target.value, setName)}
                    onBlur={() => handleBlur('name')}
                    invalid={errors.name && touched.name}
                  />
                  {errors.name && touched.name && (
                    <FormFeedback className="text-red-400 text-sm mt-1">
                      Nome é obrigatório
                    </FormFeedback>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label className="text-sm text-gray-300 text-font-family">Email *</Label>
                  <Input 
                    type="email"
                    placeholder="seu@email.com" 
                    className={`bg-[#1d1d1d] border-gray-700 ${errors.email && touched.email ? 'border-red-500' : ''}`}
                    value={email} 
                    onChange={e => handleChange('email', e.target.value, setEmail)}
                    onBlur={() => handleBlur('email')}
                    invalid={errors.email && touched.email}
                  />
                  {errors.email && touched.email && (
                    <FormFeedback className="text-red-400 text-sm mt-1">
                      {!email.trim() ? 'Email é obrigatório' : 'Email inválido'}
                    </FormFeedback>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label className="text-sm text-gray-300 text-font-family">Assunto</Label>
                  <Input 
                    placeholder="Sobre o que quer falar?" 
                    className="bg-[#1d1d1d] border-gray-700"
                    value={subject} 
                    onChange={e => setSubject(e.target.value)}
                    onBlur={() => handleBlur('subject')}
                  />
                </FormGroup>

                <FormGroup>
                  <Label className="text-sm text-gray-300 text-font-family">Mensagem *</Label>
                  <Input
                    type="textarea"
                    placeholder="Conte-me mais sobre seu projeto ou dúvida..."
                    className={`bg-[#1d1d1d] border-gray-700 h-36 ${errors.message && touched.message ? 'border-red-500' : ''}`}
                    value={message} 
                    onChange={e => handleChange('message', e.target.value, setMessage)}
                    onBlur={() => handleBlur('message')}
                    invalid={errors.message && touched.message}
                  />
                  {errors.message && touched.message && (
                    <FormFeedback className="text-red-400 text-sm mt-1">
                      Mensagem é obrigatória
                    </FormFeedback>
                  )}
                </FormGroup>

                <div className={`flex mb-4 items-start gap-2 text-sm p-2 rounded ${errors.acceptedTerms && touched.acceptedTerms ? 'bg-red-900/20 border border-red-500/50' : ''}`}>
                  <input 
                    type="checkbox" 
                    className="mt-1 flex-shrink-0 text-font-family"
                    checked={acceptedTerms}
                    onChange={e => handleChange('acceptedTerms', e.target.checked, setAcceptedTerms)}
                    onBlur={() => handleBlur('acceptedTerms')}
                  /> 
                  <span className={`text-font-family ${errors.acceptedTerms && touched.acceptedTerms ? 'text-red-400' : 'text-gray-400'}`}>
                    {" "} Concordo em compartilhar meus dados para que o responsável possa responder
                    minha mensagem. Seus dados não serão compartilhados com terceiros.
                    {errors.acceptedTerms && touched.acceptedTerms && (
                     <p className="text-red-400 text-sm mt-1" style={{ color: '#dc3545', fontSize: '.875em' }} >
                        Você precisa aceitar os termos para continuar
                      </p>
                    )}
                  </span>
                </div>

                <div style={{ textAlign: 'end' }}>
                  <Button 
                    style={{ backgroundColor: '#343a40', color: 'white', borderColor: 'transparent' }} 
                    className="mx-2 button mb-4" 
                    onClick={handleRegister}
                  >
                    <FontAwesomeIcon icon={faMessage} className="me-2" /> Enviar mensagem
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>  

          <Col sm="6">
            {/* Informações de Contato */}
            <div className="flex flex-col gap-6">
              <Card className="bg-[#151515] border border-gray-800 rounded-2xl">
                <CardBody className="p-6 text-gray-300">
                  <h2 className="text-lg font-semibold mb-3 text-font-family">Outras formas de contato</h2>
                  <p className="text-xs mb-1 text-gray-400 text-font-family">E-mail</p>
                  <p className="mb-4 text-teal-300 text-font-family">contato@zauso-dev.com.br</p>

                  <p className="text-xs mb-1 text-gray-400 text-font-family">Contatos</p>
                  <div className="flex gap-3 mt-2">
                    <SocialIcons />
                  </div>
                </CardBody>
              </Card>
            </div>
          </Col>  
        </Row>  
      </div>
    </Container>
  );
}